import { agencyRoutes, billingRoutes, orgRoutes, settingsRoutes } from '@/routes/org-routes.const'

import type { GlobalSearchResultGroup, GlobalSearchResultItem } from './global-search.interface'
import {
  billingItemList,
  navigationItemList,
  profileItemList,
  settingsItemList,
} from './global-search-data.util'

const filterItemList = (
  itemList: GlobalSearchResultItem[],
  query: string
): GlobalSearchResultItem[] =>
  itemList.filter(
    (item) =>
      item.title.toLowerCase().includes(query) || item.description?.toLowerCase().includes(query)
  )

/**
 * Builds search result groups filtered by the search query
 */
export const buildSearchResultGroups = (searchQuery: string): GlobalSearchResultGroup[] => {
  const query = searchQuery.toLowerCase().trim()
  if (!query) {
    return []
  }

  return [
    {
      type: 'navigation',
      label: 'Pages',
      itemList: filterItemList(navigationItemList, query),
    },
    {
      type: 'profile',
      label: 'Profile',
      itemList: filterItemList(profileItemList, query),
    },
    {
      type: 'settings',
      label: 'Settings',
      itemList: filterItemList(settingsItemList, query),
    },
    {
      type: 'billing',
      label: 'Billing',
      itemList: filterItemList(billingItemList, query),
    },
  ]
}

const NAVIGATION_PATH_MAP: Record<string, string> = {
  chat: '/',
  agencies: `/${orgRoutes.agencies}/${agencyRoutes.explore}`,
  'prompt-templates': `/${orgRoutes.agencies}/${agencyRoutes.templates}`,
}

const BILLING_PATH_MAP: Record<string, string> = {
  overview: `/${billingRoutes.billing}`,
  plans: `/${billingRoutes.billing}/${billingRoutes.plans}`,
  'top-up': `/${billingRoutes.billing}/${billingRoutes.topUp}`,
  'redeem-code': `/${billingRoutes.billing}/${billingRoutes.redeemCode}`,
  usage: `/${billingRoutes.billing}/${billingRoutes.usage}`,
}

const PROFILE_PATH_MAP: Record<string, string> = {
  general: `/${orgRoutes.profile}/${settingsRoutes.general}`,
}

const buildConversationPath = (itemId: string, agencySlug?: string | null): string => {
  if (agencySlug) {
    return `/${orgRoutes.chat}/${agencySlug}/${itemId}`
  }
  return `/${orgRoutes.conversation}/${itemId}`
}

/**
 * Returns the navigation path for a search result item.
 * For prompt_template type, appends ?q={itemTitle} to pre-fill the table search.
 * For conversation type, builds path based on agency_slug presence.
 */
export const getSearchResultPath = (type: string, item: GlobalSearchResultItem): string => {
  const templateQuery = item.title ? `?q=${encodeURIComponent(item.title)}` : ''

  const pathMap: Record<string, string | undefined> = {
    navigation: NAVIGATION_PATH_MAP[item.id],
    profile: PROFILE_PATH_MAP[item.id],
    settings: `/${orgRoutes.settings}/${item.id}`,
    billing: BILLING_PATH_MAP[item.id],
    marketplace_agency: `/${orgRoutes.agencies}/${agencyRoutes.explore}/${item.id}`,
    organization_agency: `/${orgRoutes.agencies}/${agencyRoutes.organization}/${item.id}`,
    draft_agency: `/${orgRoutes.agencies}/${agencyRoutes.studio}/${item.id}`,
    recent_agency: `/${orgRoutes.agencies}/${agencyRoutes.explore}/${item.id}`,
    prompt_template: `/${orgRoutes.agencies}/${agencyRoutes.templates}${templateQuery}`,
    conversation: buildConversationPath(item.id, item.agencySlug),
  }
  return pathMap[type] ?? ''
}
